import { useFormikContext } from 'formik';
import { Action, FormGroup, Grid, useAssetTranslation } from '@mss/mss-component-library';
import React, { useMemo, useState } from 'react';
import { ActionServiceForm } from './Forms';
import { ActionServiceSelector, Select } from './Inputs';
import { DeleteContainer } from './ConditionForm';
import { Plus, Trash } from 'react-feather';
import { InfoCircleFilled } from '@ant-design/icons';
import classNames from 'classnames';

type Props = {
  index?: number;
  action: Action;
  last: boolean | string;
  addAction: (index: number) => (e: any) => void;
  removeAction: (index: number) => (e: any) => void;
  disabled: string;
};

type AddActionButtonProps = {
  empty?: boolean;
};

export const AddActionButton = ({
  className,
  empty,
  ...props
}: React.ComponentProps<'button'> & AddActionButtonProps) => {
  return (
    <button
      className={classNames(
        'bg-[#fbfbfb] flex items-center justify-center p-[5px] border-none text-[#96a9a7] text-[18px]',
        {
          'w-full ml-0 mb-0 border-t-0': empty,
          'w-[calc(100%+20px)] ml-[-10px] mb-[-10px] border-t-[10px] border-[#eef1f0]':
            !empty,
        },
        className
      )}
      {...props}
    />
  );
};

export const EmailInfo = ({
  className,
  ...props
}: React.ComponentProps<'div'>) => {
  return (
    <div
      className={classNames(
        'mt-[20px] bg-[#fbfbfb] border border-[#96a9a7] rounded-[2px] flex items-center justify-center w-full p-[2px]',
        '[&>span]:leading-[1.3] [&>span]:text-[12px] [&>span]:ml-[10px]',
        className
      )}
      {...props}
    />
  );
};

export const ActionTypeSelector = ({
  index,
  disabled,
}: {
  index?: number;
  disabled: string;
}) => {
  const { setFieldValue } = useFormikContext();
  const { t } = useAssetTranslation();

  return (
    <Select
      disabled={!!disabled}
      name={`action${index != null ? `s.${index}` : ''}.type`}
      data-ta={'Actions_ActionType'}
      onChange={() =>
        index != null
          ? setFieldValue(`actions.${index}.settings`, {})
          : setFieldValue(`action.settings`, {})
      }
    >
      <Select.Option value="CALL_SERVICE">{t('Call service')}</Select.Option>
    </Select>
  );
};

const ActionForm = ({
  action,
  index = 0,
  last,
  addAction,
  removeAction,
  disabled,
}: Props) => {
  const { t } = useAssetTranslation();
  const [selectedService, setSelectedService] = useState('');
  const [subTypeSelector, actionForm] = useMemo(() => {
    const components = {
      CALL_SERVICE: [
        <ActionServiceSelector
          index={index}
          type="action"
          disabled={disabled}
        />,
        <ActionServiceForm
          action={action}
          index={index}
          type="action"
          onSelectedServiceChange={service => setSelectedService(service)}
          disabled={disabled}
        />,
      ],
    };
    return components[action.type as keyof typeof components] || [<></>, <></>];
  }, [index, action.type]);

  const isLast = ['true', true].includes(last);
  return (
    <Grid.Row
      className={classNames('p-[10px]', {
        'border-b-0': isLast,
        'border-b-[10px] border-[#eef1f0]': !isLast,
      })}
    >
      {disabled !== 'disabled' && (
        <Grid.Row className="w-full justify-end items-center">
          <DeleteContainer onClick={removeAction(index)}>
            <Trash size={14} color="red" />
          </DeleteContainer>
        </Grid.Row>
      )}
      <Grid.Col width={4}>
        <FormGroup
          label={t('Action type',{ns:'configuration'})}
          input={<ActionTypeSelector index={index} disabled={disabled} />}
        />
        {subTypeSelector}
        {selectedService === 'email' && (
          <EmailInfo>
            <InfoCircleFilled style={{ color: '#417e80' }} />
            <span>
              {t(
                'This service is limited to send-out maximum one email per 60 minutes.',{ns:'configuration'}
              )}
            </span>
          </EmailInfo>
        )}
      </Grid.Col>
      <Grid.Col width={8}>
        <Grid.Row>{actionForm}</Grid.Row>
      </Grid.Col>
      {last && (
        <Grid.Col width={12}>
          <AddActionButton
            disabled={!!disabled}
            onClick={addAction(index + 1)}
            data-ta="addConditionButton"
          >
            <Plus color="#96A9A7" /> {t('Add action',{ns:'configuration'})}
          </AddActionButton>
        </Grid.Col>
      )}
    </Grid.Row>
  );
};

export default ActionForm;
